import Image from 'next/image'
import isEmpty from 'lodash/isEmpty'
import {
  Tile,
  TileCaption,
  TileImage,
  TileOverlay,
  Nameplate,
} from '@mc/design-system'
import { SOURCES } from '@mc/share'
import { useStaticData } from '@mc/static-data-react'
import { EventTypes, PageSource } from '@mc/track-event'
import CTA from 'components/CTA'
import SampleAction from 'components/HighContrastHero/components/SampleAction'
import ShareAction from 'components/HighContrastHero/components/ShareAction'
import TrailerAction from 'components/HighContrastHero/components/TrailerAction'
import { TrackProps } from 'components/HighContrastHero/events'
import { PricingText } from 'components/PricingText'
import { SeriesLogo } from 'components/SeriesLogo'
import { getCourseModalText } from 'components/VideoModal/getCourseModalText'
import { DynamicHeroValues, useCMDynamicHero } from 'libs/dynamic-hero'
import { type Course } from 'types/data'
import CadenceBadge from '../CadenceBadge/CadenceBadge'
import styles from './Hero.module.scss'

const getTrackProps = (course: Course): TrackProps => ({
  eventName: EventTypes.COURSE_MARKETING_CLICK,
  slug: course.slug,
  pageCategory: 'class',
  pageSource: PageSource.CLASS_PAGE,
})

const getTrailerProps = (course: Course) => ({
  brightcoveVideoId: course.brightcoveVideoId,
  mediaUuid: course.mediaUuid,
  ...getCourseModalText(course),
  id: course.id,
  slug: course.slug,
  title: course.title,
  instructorAvatar: course.primary1x1,
})

const getSampleProps = (course: Course) => ({
  brightcoveVideoId: course?.sample?.brightcoveVideoId,
  mediaUuid: course?.sample?.mediaUuid,
  ...getCourseModalText(course),
  id: course.id,
  title: course.title,
  slug: course.slug,
})

const getShareProps = (course: Course) => ({
  slug: course.slug ?? '',
  instructorName: course.instructorName ?? '',
  id: course.id ?? '',
  headline: course.headline ?? '',
  source: SOURCES.CLASS_GUEST_NEXT,
})

export const Hero = () => {
  const { course } = useStaticData<{ course: Course }>()
  const dynamicHero = useCMDynamicHero()

  if (!course?.cinematic12x5) return <></>

  return (
    <section data-testid='hero-section'>
      <div className={styles.tileContainer}>
        <Tile aspectRatio='auto'>
          <div className='d-block d-md-none'>
            <TileImage
              image={<Image alt='' src={course.image2x3 || ''} fill priority />}
            />
          </div>
          <div className='d-none d-md-block'>
            <TileImage
              image={<Image alt='' src={course.cinematic12x5} fill priority />}
            />
          </div>
          <TileOverlay color='0,0,0' className={styles.seriesOverlay} />
          <TileCaption>
            <div className='container mc-text--center'>
              <div className='row justify-content-center'>
                <NameplateSection course={course} />
              </div>
              <div className='row d-flex justify-content-center'>
                <div className='col-12 col-md-8 col-lg-6'>
                  {dynamicHero.subheader || course.marketingCmColumn}
                </div>
              </div>

              <div className='row justify-content-center mc-mt-4'>
                <div className='col-auto'>
                  {(course.brightcoveVideoId || course.mediaUuid) && (
                    <TrailerAction
                      trailer={getTrailerProps(course)}
                      trackProps={getTrackProps(course)}
                    />
                  )}
                  <SampleAction
                    sample={getSampleProps(course)}
                    trackProps={getTrackProps(course)}
                  />
                  <ShareAction
                    share={getShareProps(course)}
                    trackProps={getTrackProps(course)}
                  />
                </div>
              </div>
            </div>

            <div className='container'>
              <div className='row justify-content-center mc-text--center'>
                <div className='col-md-5 col-lg-3'>
                  <CTA
                    fullWidth
                    trackProps={{
                      category: 'video-playlist-module',
                      content_type: 'product',
                      location: 'hero',
                      page_source: 'Course Marketing',
                    }}
                  />
                  <p className='mc-text-small mc-opacity--hinted mc-mt-3'>
                    <PricingText translationKey='longWithSeries' />
                  </p>
                </div>
              </div>
            </div>
          </TileCaption>
        </Tile>
      </div>
    </section>
  )
}

const NameplateSection = ({ course }: { course: Course }) => {
  const { savedDynamicHero } = useStaticData<{
    savedDynamicHero: DynamicHeroValues
  }>()
  const { header } = useCMDynamicHero({
    useCourseDefaultValues: true,
  })

  const isDynamicNameplate =
    !isEmpty(savedDynamicHero) &&
    savedDynamicHero?.nameplate !== course.nameplate

  if (isDynamicNameplate) {
    return (
      <div className='col-9 col-sm-7'>
        <CadenceBadge />
        <SeriesLogo width={182} />
        <div className='mc-text--brand mc-text-b1 mc-text-d1'>{header}</div>
      </div>
    )
  }

  return (
    <div className='col-8 col-sm-6 col-md-3'>
      <div className='d-flex flex-column align-items-center justify-content-center'>
        <CadenceBadge />
        <SeriesLogo width={182} />
        <Nameplate
          size='small'
          image={
            // PEN-6909: mc-w-100 gives the image a definite width so the
            // aspect-ratio below can reserve height before the image loads.
            <h1
              aria-label={course.title || ''}
              className='mc-w-100 d-flex align-items-end justify-content-center position-relative'
            >
              <Image
                alt=''
                className={styles.nameplateImage}
                src={course.nameplate || ''}
                fill
                // PEN-6909: reserve exact layout space to prevent CLS
                style={
                  course.nameplateAspectRatio
                    ? { aspectRatio: course.nameplateAspectRatio }
                    : undefined
                }
                sizes='220px'
                priority
              />
            </h1>
          }
          subText={course.displaySubtitle || header || ''}
          separator={true}
          className='mc-text--center'
          align='bottom'
        />
      </div>
    </div>
  )
}
